import { createContext, useCallback, useContext, useRef } from 'react';

/**
 * MapPaneContext — set by a GM Studio map pane around its canvas (package 5.3);
 * absent at the table, where the one canvas is the only one.
 *
 * Studio can show two live maps side by side. Each has its own engine and
 * hooks; what stays global is the keyboard and the DOM. This context lets the
 * canvas cluster ask the two questions that need a pane:
 *  - `ownsKeyboard()` — is this the working map, the one a canvas hotkey acts
 *    on? Exactly one pane answers yes, so one gesture is one action.
 *  - `root` — the pane's DOM root, for lookups that used a document-wide id
 *    (`#zoom-controls`, `#coordinates`, the hidden upload input); the second
 *    pane's copies of those ids would otherwise land in the first pane.
 */
export interface MapPaneValue {
  readonly pageId: string;
  ownsKeyboard(): boolean;
  readonly root: HTMLElement | null;
}

export const MapPaneContext = createContext<MapPaneValue | null>(null);

/** The surrounding Studio map pane, or null at the table. */
export function useMapPane(): MapPaneValue | null {
  return useContext(MapPaneContext);
}

/**
 * Canvas hotkey gate: a stable function, true when this canvas may act on a
 * key press — always at the table, in Studio only in the working map pane.
 */
export function useCanvasKeyboardOwner(): () => boolean {
  const pane = useMapPane();
  const paneRef = useRef(pane);
  paneRef.current = pane;
  return useCallback(() => paneRef.current?.ownsKeyboard() ?? true, []);
}

/** A document-wide lookup, scoped to the surrounding map pane when there is one. */
export function useCanvasScopedQuery(): <E extends Element>(selector: string) => E | null {
  const pane = useMapPane();
  const paneRef = useRef(pane);
  paneRef.current = pane;
  return useCallback(<E extends Element>(selector: string): E | null => {
    const current = paneRef.current;
    if (current === null) return document.querySelector<E>(selector);
    return current.root?.querySelector<E>(selector) ?? null;
  }, []);
}
